import React, { FC } from 'react';
import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { FormikValues } from 'formik';

import { Group } from 'api';
import { UserGroup } from 'types/app';
import { useCurrentUser } from 'hooks';
import { GroupAvatar, UserAvatar } from 'components';
import { USER_FEED_ID } from 'constants/index';

interface IProps extends WithStyles<typeof styles> {
  anchorEl: HTMLElement;
  formValues: FormikValues;
  onClose: () => void;
  groups: UserGroup[];
  onGroupToggle: (group: UserGroup) => void;
}

const styles = (theme: Theme) =>
  createStyles({
    menuPadding: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    item: {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
      },
      '&$selected': {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    itemText: {
      marginRight: theme.spacing(),
    },
    reset: {
      marginRight: 0,
    },
    selected: {},
  });

const GroupSelect: FC<IProps> = props => {
  const currentUser = useCurrentUser();

  const { classes, anchorEl, formValues, groups, onGroupToggle, onClose } = props;
  const { item, selected } = classes;

  function isTimeline(group: UserGroup) {
    return group.id === USER_FEED_ID;
  }

  function renderAvatar(group: UserGroup) {
    return isTimeline(group) ? (
      <UserAvatar user={currentUser} />
    ) : (
      <GroupAvatar group={group as Group} />
    );
  }

  function renderGroup(group: UserGroup) {
    const { group_ids } = formValues;
    const isSelected = group_ids.includes(group.id);

    return (
      <MenuItem
        key={group.id}
        classes={{ root: item, selected }}
        selected={isSelected}
        onClick={() => onGroupToggle(group)}>
        <ListItemIcon>{renderAvatar(group)}</ListItemIcon>
        <ListItemText
          className={classes.itemText}
          primaryTypographyProps={{ variant: 'inherit' }}
          primary={group.title}
        />
        <ListItemIcon className={classes.reset}>
          <Checkbox checked={isSelected} />
        </ListItemIcon>
      </MenuItem>
    );
  }

  return (
    <Menu
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      MenuListProps={{ classes: { padding: classes.menuPadding } }}>
      {groups.map(renderGroup)}
    </Menu>
  );
};

export default withStyles(styles)(GroupSelect);
